/**
 * ADMIN-T014 — route lifecycle phase events per SPEC §7.2.
 *
 * For M1 we emit to a local sink only (console + in-memory ring buffer).
 * Remote dispatch is wired up in ADMIN-T075.
 */

export type RoutePhase =
  | 'RouteResolving'
  | 'RouteGuarding'
  | 'ChunkLoading'
  | 'DataPriming'
  | 'Interactive'
  | 'RouteSucceeded'
  | 'RouteDenied'
  | 'ChunkLoadFailed';

export interface RoutePhaseEvent {
  phase: RoutePhase;
  at: number;
  payload: Record<string, unknown>;
}

const BUFFER_LIMIT = 200;
const buffer: RoutePhaseEvent[] = [];
const subscribers = new Set<(e: RoutePhaseEvent) => void>();

export function emitRoutePhase(phase: RoutePhase, payload: Record<string, unknown> = {}): void {
  const event: RoutePhaseEvent = { phase, at: performance.now(), payload };
  buffer.push(event);
  if (buffer.length > BUFFER_LIMIT) buffer.shift();
  for (const fn of subscribers) {
    try {
      fn(event);
    } catch {
      // Subscriber errors must not break navigation.
    }
  }
  if (typeof import.meta !== 'undefined' && import.meta.env?.DEV) {
    console.debug('[route]', phase, payload);
  }
}

export function onRoutePhase(fn: (e: RoutePhaseEvent) => void): () => void {
  subscribers.add(fn);
  return () => subscribers.delete(fn);
}

export function getRoutePhaseBuffer(): readonly RoutePhaseEvent[] {
  return buffer;
}

export function clearRoutePhaseBuffer(): void {
  buffer.length = 0;
}
